import type { Ref } from 'vue';
import type { CanEntitlement } from '#layers/access/public';
import type { FetchFunction } from '#layers/base/public';
import type { Filter, PrimitivePrimaryKey } from '#layers/data-model/public';
import type { FormSaveContext, FormSaveError } from '#layers/forms/public';
import type { CollectionPickerResult } from '#layers/listing/public';
import { useOverlay } from '@nuxt/ui/composables';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { allowsResource, getKeyId } from '#layers/access/public';
import { SortDirection, toStructuredError } from '#layers/base/public';
import { useCollectionByName, usePaginatedListing } from '#layers/data-model/public';
import { injectFormBlockContext } from '#layers/forms/public';
import { LazyCollectionPickerDrawer } from '#layers/listing/lazy';
import { useWorkspaceRuntimeHandle } from '#layers/runtime/public';
import { buildAssignmentPickerFilter } from './assignment-filter';
import { buildAssignmentDisplayItems } from './build-assignment-display-items';
import { disconnectJunctionAssignments } from './disconnect-junction-assignments';
import { findUnassignedIds } from './find-unassigned-ids';
import { useAssignmentEntitlement } from './use-assignment-entitlement';
import { usePendingConnections } from './use-pending-connections';

/** How an assignment points back at the owner it is assigned to. */
interface AssignmentRelation {
	/** Relation field on the assignment, e.g. `roles` on a policy. */
	field: string;
	/** Key inside that relation holding the owner id, e.g. `roleId`. */
	ownerKey: string;
}

/** The junction rows linking an owner to its assignments. */
interface AssignmentJunction {
	/** RStore collection API name holding the junction rows. */
	collectionApiName: string;
	/** Junction field holding the owner id. */
	ownerKey: string;
	/** Junction field holding the assignment id. */
	targetKey: string;
}

interface AssignmentMutationOptions {
	fetch: FetchFunction;
	ids: string[];
	ownerId: string;
}

interface AssignmentBlockOptions {
	/**
	 * RStore collection the assignments are queried from and picked out of.
	 *
	 * May differ from `displayCollectionName`: policies are read through the raw items endpoint,
	 * which is the only one honouring the entitlement filter and pagination.
	 */
	collectionApiName: string;
	/** Schema collection the list card and picker drawer render. */
	displayCollectionName: string;
	/**
	 * Owner whose assignments this block edits: a role for policies, a member for roles.
	 *
	 * This is the key of the item the form is editing, unset for an unsaved item. The save handler
	 * uses the key the form reports on submit instead.
	 */
	ownerId: Ref<PrimitivePrimaryKey | undefined>;
	/** Entitlement allowlist gating which ids may be assigned. */
	entitlement: CanEntitlement;
	relation: AssignmentRelation;
	junction: AssignmentJunction;
	/** Fields to fetch and show beyond the id and the name. */
	fields?: string[];
	/** Ids the picker never offers, regardless of entitlements. */
	excludedIds?: string[];
	/** Attaches the ids to the owner. */
	attach: (options: AssignmentMutationOptions) => Promise<unknown>;
	/** Detaches the ids from the owner. */
	detach: (options: AssignmentMutationOptions) => Promise<unknown>;
	/** i18n keys for the two save failures. */
	errorMessages: { attach: string; detach: string };
}

interface AssignmentItem {
	id: string;
	name?: string;
}

/**
 * The whole behavior of a relation-assignment block: policies on a role, roles on a member.
 *
 * Owns the paginated list of persisted assignments, the staged additions and removals, the picker
 * round-trip, the save handler, and the props the list card renders with. The two blocks differ
 * only in which collection they read, how an assignment names its owner, and which API attaches
 * and detaches — so those are all this takes.
 */
export function useAssignmentBlock<TItem extends AssignmentItem>(options: AssignmentBlockOptions) {
	const { ownerId, relation, junction } = options;
	const { t } = useI18n();

	/** False while the owning record is unsaved, which is the create-mode state of every caller. */
	const hasOwner = computed(() => Boolean(ownerId.value));

	const workspaceHandle = useWorkspaceRuntimeHandle();
	const store = workspaceHandle.store;
	const formBlockContext = injectFormBlockContext();

	const { collection } = useCollectionByName(options.displayCollectionName);
	const assignmentCollection = store.$collection(() => options.collectionApiName);
	const queryFields = computed(() => ['id', 'name', ...options.fields ?? []]);

	/** Matches assignments already held by `owner`. */
	function assignedFilter(owner: PrimitivePrimaryKey): Filter {
		return { [relation.field]: { _some: { [relation.ownerKey]: owner } } };
	}

	/** Matches assignments not held by `owner`, which is what the picker may offer. */
	function unassignedFilter(owner: PrimitivePrimaryKey): Filter {
		return { [relation.field]: { _none: { [relation.ownerKey]: owner } } };
	}

	const {
		allowedAssignableIds,
		canAssign,
		canDisconnectItem,
	} = useAssignmentEntitlement(options.entitlement);

	// Cast past Vue's ref unwrapping, which would otherwise rewrite the generic item type.
	const pendingItemData = ref(new Map<string, TItem>()) as Ref<Map<string, TItem>>;

	const {
		items: persistedItems,
		collectionQuery,
		pageNumber,
		pageSize,
		totalItemsCount: persistedTotalItemsCount,
		currentItemsCount: persistedCurrentItemsCount,
		isLoading,
		refresh,
	} = usePaginatedListing<TItem>({
		store,
		collectionApiName: options.collectionApiName,
		resetOn: ownerId,
		createQuery: (queryPageSize) => assignmentCollection.query((q) => {
			// An unsaved owner has no assignments. Without this guard, RStore drops the undefined
			// relation id and queries the whole collection.
			if (!ownerId.value) return q.many({ enabled: false });

			return q.many({
				fields: queryFields.value,
				filter: assignedFilter(ownerId.value),
				// TODO(hannes) no-cache required here since we can't resolve relational filters locally
				fetchPolicy: 'no-cache',
				resultMode: 'responseRefs',
				pageIndex: 0,
				pageSize: queryPageSize.value,
				sort: [{ name: { direction: SortDirection.Ascending } }, { id: { direction: SortDirection.Ascending } }],
			});
		}),
	});

	const persistedIds = computed(() => new Set((collectionQuery.data.value ?? []).map(({ id }) => id)));

	const {
		pendingConnections,
		pendingDisconnections,
		hasChanges,
		connect,
		disconnect,
		undo,
		getItemState,
		captureChanges,
		clear,
	} = usePendingConnections({ isPersisted: (id) => persistedIds.value.has(id) });

	watch(ownerId, () => {
		clear();
		pendingItemData.value.clear();
	}, { flush: 'sync' });

	const stagedItems = computed(() =>
		[...pendingItemData.value.values()].filter(({ id }) => pendingConnections.value.has(id)),
	);

	const displayItems = computed(() => buildAssignmentDisplayItems({
		pageItems: persistedItems.value,
		pageNumber: pageNumber.value,
		pendingItems: stagedItems.value,
		getItemState,
	}));

	// Staged additions belong to no server page, so they extend the persisted counts instead of
	// participating in them - the same arithmetic relation fields use for unsaved rows. They are
	// reported separately as `unpagedItems` so the pagination controls can subtract them back out;
	// counting them as paged rows would offer a page the query cannot serve.
	const unpagedItemsCount = computed(() => pendingConnections.value.size);

	const totalItemsCount = computed(() =>
		persistedTotalItemsCount.value != null
			? persistedTotalItemsCount.value + unpagedItemsCount.value
			: undefined,
	);

	const currentItemsCount = computed(() => persistedCurrentItemsCount.value + unpagedItemsCount.value);

	async function selectAssignments() {
		const targetOwnerId = ownerId.value;
		if (!collection.value || !targetOwnerId) return;

		const modal = useOverlay().create(LazyCollectionPickerDrawer, {
			destroyOnClose: true,
			props: {
				collectionId: collection.value.id,
				collectionApiName: options.collectionApiName,
				...(options.fields ? { fields: options.fields } : {}),
				filter: buildAssignmentPickerFilter(
					allowedAssignableIds.value,
					[...pendingConnections.value, ...options.excludedIds ?? []],
					unassignedFilter(targetOwnerId),
					[...pendingDisconnections.value],
				) satisfies Filter,
			},
		});

		const result = await modal.open().result as CollectionPickerResult<TItem> | undefined;
		// The owner can change while the drawer is open, which clears the staged state; results
		// picked for the previous owner must not be staged onto the new one.
		if (!result || ownerId.value !== targetOwnerId) return;

		const itemsById = new Map(result.items.map((item) => [item.id, item]));

		for (const key of result.selection) {
			const id = getKeyId(key)!;
			const item = itemsById.get(id);
			if (!item) continue;

			// Only retain row data for actually staged additions, so the map mirrors pendingConnections.
			if (connect(id)) {
				pendingItemData.value.set(id, item);
			}
		}
	}

	function removeAssignment(item: { id: string }) {
		const wasPending = pendingConnections.value.has(item.id);
		disconnect(item.id);
		if (wasPending) pendingItemData.value.delete(item.id);
	}

	function undoAssignment(item: { id: string }) {
		undo(item.id);
		pendingItemData.value.delete(item.id);
	}

	/**
	 * Ids among `candidateIds` that `owner` already holds, re-checked at save time.
	 *
	 * Single-shot on purpose: a reactive query created here has no owning scope, so it would stay
	 * subscribed and refetch on every later cache reset.
	 */
	async function loadAssignedIds(owner: string, candidateIds: string[]): Promise<string[]> {
		const assigned = await assignmentCollection.findMany({
			fields: ['id'],
			filter: { id: { _in: candidateIds }, ...assignedFilter(owner) },
			fetchPolicy: 'no-cache',
			pageIndex: 0,
			pageSize: candidateIds.length,
		});

		return assigned.map(({ id }) => id);
	}

	formBlockContext.onSave(() => {
		const submitted = captureChanges();
		const savedItemData = new Map([...persistedItems.value, ...pendingItemData.value.values()].map((item) => [item.id, item]));
		const targetOwnerId = ownerId.value;
		const savedAllowlist = allowedAssignableIds.value;
		const isAllowedAtSave = (id: string) => allowsResource(savedAllowlist, id);
		const connectionsToSave = submitted.connections.filter((id) => isAllowedAtSave(id));
		const disconnectionsToSave = submitted.disconnections.filter((id) => isAllowedAtSave(id));

		return async ({ itemKey }: FormSaveContext): Promise<FormSaveError[]> => {
			const errors: FormSaveError[] = [];
			const savedOwnerId = getKeyId({ id: itemKey.id })!;

			if (connectionsToSave.length > 0) {
				try {
					// Drop ids assigned by someone else after the picker fetched its results.
					const idsToAttach = await findUnassignedIds(
						connectionsToSave,
						(candidateIds) => loadAssignedIds(savedOwnerId, candidateIds),
					);

					if (idsToAttach.length > 0) {
						await options.attach({ fetch: workspaceHandle.fetch, ownerId: savedOwnerId, ids: idsToAttach });
					}

					submitted.acknowledgeConnections(connectionsToSave);

					if (ownerId.value === targetOwnerId) {
						for (const id of connectionsToSave) {
							if (!pendingConnections.value.has(id)) pendingItemData.value.delete(id);
						}
					}
				}
				catch (error) {
					errors.push({ message: t(options.errorMessages.attach), source: toStructuredError(error) });
				}
			}

			if (disconnectionsToSave.length > 0) {
				try {
					await disconnectJunctionAssignments({
						junctionCollection: store.$collection(() => junction.collectionApiName),
						filter: { [junction.ownerKey]: savedOwnerId, [junction.targetKey]: { _in: disconnectionsToSave } },
						disconnect: () => options.detach({ fetch: workspaceHandle.fetch, ownerId: savedOwnerId, ids: disconnectionsToSave }),
					});

					submitted.acknowledgeDisconnections(disconnectionsToSave);

					if (ownerId.value === targetOwnerId) {
						for (const id of disconnectionsToSave) {
							const item = savedItemData.get(id);
							if (item && pendingConnections.value.has(id)) pendingItemData.value.set(id, item);
						}
					}
				}
				catch (error) {
					errors.push({ message: t(options.errorMessages.detach), source: toStructuredError(error) });
				}
			}

			try {
				await refresh();
			}
			catch {
				// Refresh failure is non-fatal - user can manually refresh
			}

			return errors;
		};
	});

	formBlockContext.reportHasChanges(() => hasChanges.value);

	/**
	 * Everything `RelatedItemListCard` needs, so the two blocks render an identical template.
	 *
	 * Undefined until the schema collection resolves, which is also the card's render condition —
	 * `v-bind` cannot narrow `collectionId` on its own.
	 */
	const cardProps = computed(() => collection.value && {
		'items': displayItems.value,
		'collectionId': collection.value.id,
		'showEdit': false,
		'showCreate': false,
		'showAddExisting': true,
		'permissions': {
			// The picker filters on the owner, so it cannot open before the record it assigns to
			// exists. Disable the action with a reason rather than letting the button do nothing.
			canConnect: canAssign.value && hasOwner.value,
			connectDisabledReason: hasOwner.value ? undefined : t('field_types.assignment.save_before_assigning'),
			canDisconnectItem,
		},
		'pagination': {
			page: pageNumber.value,
			pageSize: pageSize.value,
			totalItems: totalItemsCount.value,
			currentItems: currentItemsCount.value,
			unpagedItems: unpagedItemsCount.value,
		},
		'loading': isLoading.value,
		'onSelect': selectAssignments,
		'onRemove': removeAssignment,
		'onUndo': undoAssignment,
		'onUpdate:page': (page: number) => {
			pageNumber.value = page;
		},
		'onUpdate:pageSize': (size: number) => {
			pageSize.value = size;
		},
	});

	return { cardProps };
}
